import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconQuestionMarkFilled } from '@iwoca/icons';
import { Pagination, Table, Tooltip } from '@iwoca/orion';
import { ColumnDef } from '@iwoca/orion/lib/components/table/Table';
import classNames from 'classnames';
import { useSearchParams } from 'react-router';

import { CustomersMobileTable } from './CustomersMobileTable';
import styles from './CustomersTable.module.css';
import { useGetSellerCustomers } from '../../../../api/lending/lapiHooks';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { formatDate } from '../../../Fees/RateContextHeader/RateContextHeader';
import { TCustomersTableColumns, TSelectedBuyer } from '../../Customers.types';
import { CustomerInformationModal } from '../CustomerInformationModal/CustomerInformationModal';
import { CustomersStatusModal } from '../CustomersStatusModal/CustomersStatusModal';

export const CustomersTable = ({
  currentPage,
  setCurrentPage,
  searchQuery,
}: {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  searchQuery?: string;
}) => {
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [showCustomerModal, setShowCustomerModal] = useState<boolean>(false);
  const [selectedBuyer, setSelectedBuyer] = useState<TSelectedBuyer>({
    stateKey: '',
    name: '',
    applicationStage: undefined,
    applicationSubStages: undefined,
    limitAmount: null,
    limitDuration: null,
  });
  const [hasFiredTooltipEvent, setHasFiredTooltipEvent] = useState(false);
  const [searchParams] = useSearchParams();
  const { isMobile } = useIsMobile({ maxWidth: 1024 });

  const { sellerCustomers, metadata, loadingSellerCustomers } =
    useGetSellerCustomers({
      page: currentPage,
      spending_limit_status: searchParams.get('status') || undefined,
      application_stage: searchParams.get('stage') || undefined,
      search: searchQuery,
    });

  const handleClick = (
    row: TCustomersTableColumns,
    button: 'application_stage' | 'company_name',
  ) => {
    if (button === 'application_stage') {
      tracking.ampli.customersPageApplicationStageClicked();
    }
    if (button === 'company_name') {
      tracking.ampli.customersPageBuyerCompanyNameClicked();
    }

    setSelectedBuyer({
      stateKey: row.buyer_state_key,
      name: row.company_name || 'Awaiting details',
      applicationStage: row.application_stage,
      applicationSubStages: row.application_sub_stage_object,
      // TODO(types): fix in schema / add null checks.
      limitAmount: row.limit_amount!,
      limitDuration: row.limit_duration!,
    });
    setShowCustomerModal((prev) => !prev);
  };

  const getDaysSinceExpiry = (timeout: string | null) => {
    if (timeout === null) return null;

    const days = Math.ceil(
      (Date.now() - new Date(timeout).getTime()) / (1000 * 60 * 60 * 24) - 1,
    );

    return days;
  };

  const columns: ColumnDef<TCustomersTableColumns>[] = [
    {
      cell: (info) =>
        formatDate(info.row.original.earliest_interacted_date, 'short'),
      id: 'date',
      header: 'Date',
      className: classNames(styles.standardTableRow, styles.dateRow),
    },
    {
      cell: (info) => {
        if (shouldDisableModalClick(info.row.original, 'company_name'))
          return <div>{info.row.original.company_name}</div>;

        return (
          <button
            className="cursor-pointer border-none bg-transparent px-0 text-left text-s text-primary-40 underline"
            onClick={() => handleClick(info.row.original, 'company_name')}
          >
            {info.row.original.company_name}
          </button>
        );
      },
      id: 'companyName',
      header: 'Company Name',
      className: classNames(styles.standardTableRow, styles.companyRow),
    },
    {
      cell: (info) => {
        if (shouldDisableModalClick(info.row.original, 'application_stage'))
          return (
            <div
              className={`text-s ${
                info.row.original.spending_limit_status.value ===
                  'last_approved_offer' &&
                info.row.original.limit_amount !== null &&
                'text-structure-60'
              }`}
            >
              {info.row.original.application_stage.label}
            </div>
          );

        return (
          <button
            className="cursor-pointer border-none bg-transparent px-0 text-s text-primary-40 underline"
            onClick={() => handleClick(info.row.original, 'application_stage')}
          >
            {info.row.original.application_stage.label}
          </button>
        );
      },
      id: 'applicationStage',
      header: 'Application Stage',
      className: classNames(styles.standardTableRow, styles.applicationRow),
    },
    {
      cell: (info) => info.row.original.spending_limit_status.label,
      id: 'limitStatus',
      header: () => (
        <>
          <div className="flex-shrink-0">Status</div>
          <button
            className="flex-shrink-0 cursor-pointer border-none bg-transparent pl-m text-xs text-primary-40 underline"
            onClick={() => setShowStatusModal(!showStatusModal)}
          >
            What are the statuses?
          </button>
        </>
      ),
      className: classNames(styles.standardTableRow, styles.limitStatusRow),
    },
    {
      cell: (info) => {
        const { showTooltip, text: tooltipText } = handleTooltip(
          info.row.original,
        );
        const daysSinceExpiry = getDaysSinceExpiry(
          info.row.original.limit_timeout,
        );
        const showDaysSinceExpiry =
          info.row.original.spending_limit_status.value ===
            'last_approved_offer' &&
          daysSinceExpiry !== null &&
          daysSinceExpiry > 0;

        if (
          info.row.original.limit_amount === null ||
          info.row.original.spending_limit_status.value === 'declined_offer'
        )
          return (
            <div className="flex gap-l text-structure-80">
              ----------{' '}
              {showTooltip && (
                <div className="flex h-[20px] w-[20px] items-center rounded-l border border-primary-40">
                  <Tooltip
                    className="text-primary-10"
                    content={tooltipText}
                    placement={info.row.index >= 3 ? 'top' : 'bottom'}
                    onMouseEnter={() => {
                      if (hasFiredTooltipEvent) return false;
                      tracking.ampli.customersPageLeftToSpendTooltipHovered();
                      setHasFiredTooltipEvent(true);
                    }}
                    onFocus={() => {
                      if (hasFiredTooltipEvent) return false;
                      tracking.ampli.customersPageLeftToSpendTooltipHovered();
                      setHasFiredTooltipEvent(true);
                    }}
                  >
                    <IconQuestionMarkFilled
                      fill="primary-10"
                      height="18"
                      width="18"
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          );

        return (
          <div className="flex w-full flex-wrap justify-between gap-s">
            <div
              className={`${info.row.original.spending_limit_status.value === 'offer_approved' && 'text-success-mid'}`}
            >
              {displayCurrency(info.row.original.limit_amount)}
            </div>
            {!isMobile && (
              <div className="font-reg text-structure-60">
                {showDaysSinceExpiry &&
                  (daysSinceExpiry < 365
                    ? `Exp ${daysSinceExpiry}d ago`
                    : 'Exp a year+ ago')}
              </div>
            )}
          </div>
        );
      },
      id: 'limitAmount',
      header: 'Left to spend',
      className: classNames(styles.standardTableRow, styles.amountRow),
    },
  ];

  return (
    <div className="w-full">
      <CustomerInformationModal
        open={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        selectedBuyer={selectedBuyer}
      />
      <CustomersStatusModal
        open={showStatusModal}
        close={() => setShowStatusModal(false)}
      />
      {isMobile ? (
        <CustomersMobileTable
          sellerCustomers={sellerCustomers || []}
          loading={loadingSellerCustomers}
          handleClick={handleClick}
          openStatusModal={() => setShowStatusModal(true)}
        />
      ) : (
        <Table
          data={sellerCustomers || []}
          columns={columns}
          loading={loadingSellerCustomers}
          className={styles.table}
          rowClassName={styles.allRows}
        />
      )}

      {metadata?.total_pages && metadata.total_pages > 1 && (
        <Pagination
          className="mx-auto mt-2xl"
          page={currentPage}
          count={metadata?.total_pages}
          onPageChange={(currentPage) => {
            document.body.scrollTop = document.documentElement.scrollTop = 0;
            setCurrentPage(currentPage);
          }}
        />
      )}
    </div>
  );
};

export const shouldDisableModalClick = (
  row: TCustomersTableColumns,
  button: 'application_stage' | 'company_name',
) => {
  // disable for x-sell customers
  if (row.application_stage.value === 'complete' && row.limit_amount === null) {
    return true;
  }

  // disable for declined customers
  if (row.application_stage.value === 'declined') {
    return true;
  }

  if (button === 'company_name') return false;

  // disable for expired spending limit customers
  if (row.spending_limit_status.value === 'last_approved_offer') {
    return true;
  }

  return false;
};

const handleTooltip = (row: TCustomersTableColumns) => {
  // x-sell customers
  if (row.application_stage.value === 'complete' && row.limit_amount === null) {
    return {
      showTooltip: true,
      text: 'Currently unable to estimate this customers Spending Limit',
    };
  }

  // customers who haven't finished the application process
  if (['business_details', 'security'].includes(row.application_stage.value)) {
    return {
      showTooltip: true,
      text: 'A spending limit hasn’t been calculated yet.',
    };
  }
  return { showTooltip: false, text: '' };
};
